import React, { useState, useRef } from "react";
import {
  FiUser,
  FiMail,
  FiMessageSquare,
  FiTag,
  FiSend,
  FiPhone,
} from "react-icons/fi";
import emailjs from "@emailjs/browser";
import "./ContactSection.css";

const ContactSection = () => {
  const form = useRef();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    subject: "",
    message: "",
  });

  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim())
      newErrors.firstName = "Le prénom est requis";
    if (!formData.lastName.trim()) newErrors.lastName = "Le nom est requis";

    if (!formData.email.trim()) {
      newErrors.email = "L'email est requis";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Format d'email invalide";
    }

    if (!formData.subject.trim()) newErrors.subject = "Le sujet est requis";
    if (!formData.message.trim()) newErrors.message = "Le message est requis";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    // Effacer l'erreur lorsque l'utilisateur modifie le champ
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateForm()) {
      setIsSubmitting(true);

      // Configurer les paramètres pour EmailJS
      const templateParams = {
        from_name: `${formData.firstName} ${formData.lastName}`,
        from_email: formData.email,
        subject: formData.subject,
        message: formData.message,
        to_email: "autoadapte@gmail.com",
      };

      // IMPORTANT: Remplacez ces valeurs par vos identifiants EmailJS
      // Vous pouvez les obtenir en vous inscrivant sur https://www.emailjs.com/
      const SERVICE_ID = "service_xmyy4qm"; // Service ID
      const TEMPLATE_ID = "template_nl09bpx"; // Template ID
      const PUBLIC_KEY = "6kUkICTYBMQUXgTcL"; // Public Key

      emailjs
        .send(SERVICE_ID, TEMPLATE_ID, templateParams, PUBLIC_KEY)
        .then((response) => {
          console.log("SUCCESS!", response.status, response.text);
          setSuccess(
            "Votre message a été envoyé avec succès! Nous vous répondrons dans les plus brefs délais."
          );
          setFormData({
            firstName: "",
            lastName: "",
            email: "",
            subject: "",
            message: "",
          });
        })
        .catch((err) => {
          console.log("FAILED...", err);
          setErrors({
            form: "Une erreur est survenue lors de l'envoi du message. Veuillez réessayer plus tard.",
          });
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    }
  };

  return (
    <div className="contact-page">
      <div className="contact-info-container">
        <h2>Nos coordonnées</h2>
        <div className="contact-info-items">
          <div className="contact-info-item">
            <FiPhone className="contact-info-icon" />
            <div>
              <h3>Whatsapp</h3>
              <p>
                <a
                  href="https://wa.me/213797267104"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="phone-link"
                >
                  +213 797 267 104
                </a>
              </p>
            </div>
          </div>
          <div className="contact-info-item">
            <FiMail className="contact-info-icon" />
            <div>
              <h3>Email</h3>
              <p>autoadapte@gmail.com</p>
            </div>
          </div>
        </div>
      </div>

      <div className="contact-form-container">
        <h2>Contactez-nous</h2>
        <p>
          Nous serons ravis de répondre à vos questions. Remplissez le
          formulaire ci-dessous et nous vous répondrons dans les plus brefs
          délais.
        </p>

        {success && <div className="success-message">{success}</div>}

        {errors.form && (
          <div className="error-message form-error">{errors.form}</div>
        )}

        <form ref={form} onSubmit={handleSubmit} className="contact-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">
                <FiUser className="input-icon" /> Prénom
              </label>
              <input
                id="firstName"
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                className={errors.firstName ? "error" : ""}
                placeholder="Votre prénom"
              />
              {errors.firstName && (
                <span className="error-message">{errors.firstName}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="lastName">
                <FiUser className="input-icon" /> Nom
              </label>
              <input
                id="lastName"
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                className={errors.lastName ? "error" : ""}
                placeholder="Votre nom"
              />
              {errors.lastName && (
                <span className="error-message">{errors.lastName}</span>
              )}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="email">
              <FiMail className="input-icon" /> Email
            </label>
            <input
              id="email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={errors.email ? "error" : ""}
              placeholder="votre.email@exemple.com"
            />
            {errors.email && (
              <span className="error-message">{errors.email}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="subject">
              <FiTag className="input-icon" /> Sujet
            </label>
            <input
              id="subject"
              type="text"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              className={errors.subject ? "error" : ""}
              placeholder="Sujet de votre message"
            />
            {errors.subject && (
              <span className="error-message">{errors.subject}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="message">
              <FiMessageSquare className="input-icon" /> Message
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              className={errors.message ? "error" : ""}
              placeholder="Écrivez votre message ici..."
              rows="5"
            ></textarea>
            {errors.message && (
              <span className="error-message">{errors.message}</span>
            )}
          </div>

          <button
            type="submit"
            className="submit-button"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <span className="loading-spinner"></span>
            ) : (
              <>
                <FiSend className="button-icon" /> Envoyer
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ContactSection;
